/**
 * Photo asset registry with configurable storage backend.
 *
 * Each photo has a fallback Unsplash URL and an optional Supabase Storage
 * path.  When `VITE_SUPABASE_STORAGE_PHOTOS_BUCKET` is set in the
 * environment, `getPhotoUrl` returns a Supabase Storage URL; otherwise it
 * falls through to the hardcoded Unsplash CDN URL.
 *
 * This is the single source of truth referenced by the backward-compatible
 * `PHOTOS` constant in `photos.ts`.
 */

import { env } from "./env";

export interface PhotoAsset {
  /** Unsplash CDN fallback URL (always available). */
  unsplash: string;
  /** Path inside the Supabase Storage bucket (e.g. "bowls.webp"). */
  storagePath?: string;
}

export const PHOTO_MAP: Record<string, PhotoAsset> = {
  bowls: {
    unsplash:
      "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=1600&q=70&auto=format&fit=crop",
    storagePath: "bowls.webp",
  },
  vegMarket: {
    unsplash:
      "https://images.unsplash.com/photo-1466637574441-749b8f19452f?w=1600&q=70&auto=format&fit=crop",
    storagePath: "veg-market.webp",
  },
  ingredients: {
    unsplash:
      "https://images.unsplash.com/photo-1505253758473-96b7015fcd40?w=1600&q=70&auto=format&fit=crop",
    storagePath: "ingredients.webp",
  },
  greens: {
    unsplash:
      "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1600&q=70&auto=format&fit=crop",
    storagePath: "greens.webp",
  },
  herbs: {
    unsplash:
      "https://images.unsplash.com/photo-1466065054553-69441b13baba?w=1600&q=70&auto=format&fit=crop",
    storagePath: "herbs.webp",
  },
  spread: {
    unsplash:
      "https://images.unsplash.com/photo-1543339308-43e59d6b73a6?w=1600&q=70&auto=format&fit=crop",
    storagePath: "spread.webp",
  },
} as const satisfies Record<string, PhotoAsset>;

/**
 * Return the best available URL for a photo key.
 *
 * Resolution order:
 *  1. Supabase Storage URL (if bucket is configured AND photo has a storagePath)
 *  2. Unsplash CDN fallback (always available)
 */
export function getPhotoUrl(key: string): string {
  const asset = PHOTO_MAP[key];
  if (!asset) {
    // Unknown key — return the first entry's Unsplash URL as a safe default
    const fallback = Object.values(PHOTO_MAP)[0]?.unsplash ?? "";
    console.warn(
      `[photo-assets] Unknown photo key "${key}", returning fallback`,
    );
    return fallback;
  }

  const bucket = env.SUPABASE_STORAGE_PHOTOS_BUCKET;
  if (bucket && asset.storagePath && env.SUPABASE_URL) {
    const base = env.SUPABASE_URL.replace(/\/$/, "");
    return `${base}/storage/v1/object/public/${bucket}/${asset.storagePath}`;
  }

  return asset.unsplash;
}
